import React, { useState, useEffect } from "react";
import { useQRScanner } from "../hooks/useQRScanner";
import type { QRScanResult } from "../hooks/useQRScanner";
import "../styles/QRScanner.css";

interface QRScannerProps {
  onScan: (result: QRScanResult) => void;
  autoStart?: boolean;
  showResult?: boolean;
}

export type { QRScanResult } from "../hooks/useQRScanner";

export function QRScanner({
  onScan,
  autoStart = true,
  showResult = true,
}: QRScannerProps) {
  const {
    isScanning,
    hasPermission,
    error,
    lastScannedCode,
    streamActive,
    isTorchSupported,
    isTorchOn,
    videoRef,
    canvasRef,
    startScanning,
    stopScanning,
    toggleTorch,
    clearError,
    clearLastScanned,
  } = useQRScanner(onScan, autoStart);

  return (
    <div className="qr-scanner-container">
      {/* Camera/Permission Status */}
      {!streamActive && (
        <div className="camera-permission-section">
          {hasPermission === null && (
            <div className="permission-pending">
              <div className="spinner"></div>
              <p>Requesting camera access...</p>
            </div>
          )}

          {hasPermission === false && (
            <div className="permission-denied">
              <div className="icon">🚫</div>
              <h3>Camera Permission Denied</h3>
              <p>
                This feature requires camera access. Please enable camera
                permissions in your browser settings and try again.
              </p>
              <button onClick={startScanning} className="btn-primary">
                Try Again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scanner UI */}
      {streamActive && (
        <div className="scanner-wrapper">
          {/* Video Feed */}
          <video
            ref={videoRef}
            className="scanner-video"
            autoPlay
            playsInline
            muted
          />

          {/* Scan Frame Overlay */}
          <div className="scan-frame">
            <div className="scan-corner scan-corner-top-left"></div>
            <div className="scan-corner scan-corner-top-right"></div>
            <div className="scan-corner scan-corner-bottom-left"></div>
            <div className="scan-corner scan-corner-bottom-right"></div>
            <div className="scan-line"></div>
          </div>

          {/* Controls */}
          <div className="scanner-controls">
            {streamActive && (
              <>
                {isTorchSupported && (
                  <button
                    onClick={toggleTorch}
                    className={`btn-control ${isTorchOn ? "torch-on" : ""}`}
                    title="Toggle flashlight"
                  >
                    {isTorchOn ? "🔦 Off" : "🔦 On"}
                  </button>
                )}
                <button
                  onClick={stopScanning}
                  className="btn-control btn-stop"
                  title="Stop scanning"
                >
                  ⏹️ Stop
                </button>
              </>
            )}
          </div>

          {/* Instructions */}
          <div className="scanner-info">
            <p>📱 Point camera at QR code</p>
          </div>
        </div>
      )}

      {/* Hidden Canvas for QR Detection */}
      <canvas ref={canvasRef} className="scanner-canvas-hidden" />

      {/* Error Display */}
      {error && (
        <div className="scanner-error">
          <span>⚠️ {error}</span>
          <button onClick={clearError} className="btn-close">
            ✕
          </button>
        </div>
      )}

      {/* Scan Result */}
      {showResult && lastScannedCode && (
        <div className="scanner-result">
          <div className="result-content">
            <div className="result-icon">✓</div>
            <div className="result-details">
              <div className="result-label">QR Code Detected</div>
              <div className="result-type">{lastScannedCode.type}</div>
              <div className="result-data">{lastScannedCode.data}</div>
              <div className="result-time">
                {lastScannedCode.timestamp.toLocaleTimeString()}
              </div>
            </div>
            <button
              onClick={clearLastScanned}
              className="btn-close"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Status Indicator */}
      <div className="scanner-status">
        <div className={`status-dot ${streamActive ? "active" : "inactive"}`}></div>
        <span>{isScanning ? "Scanning..." : "Ready"}</span>
      </div>
    </div>
  );
}
